<template>
  <div :class="['input-component', `size--${this.size}`]">
    <div :class="classes.container">
      <div :class="classes.activeBorder" v-if="prefix">
        <slot></slot>
      </div>

      <input
        :id="attributes.id"
        :class="classes.input"
        :value="value"
        v-bind="attributes"
        @keypress="$emit('keypress', $event)"
        @input="$emit('input', $event.target.value)"
        @focus="handleFocus"
        @blur="handleBlur"
        @click="$emit('click', $event)"
        autocomplete="off"
      />

      <div class="input-component__closeIcon" @click="$emit('closeIcon', $event)" v-if="hasInput">
        <CloseSvg />
      </div>

      <div :class="classes.activeBorder" v-if="suffix">
        <slot></slot>
      </div>
    </div>
    <label v-if="label" :class="classes.label" :for="attributes.id" v-html="label" />

    <div v-if="errorMessage" :for="attributes.id" :class="classes.message">
      <AlertCircleSvg class="message__icon" />
      {{ errorMessage }}
    </div>
  </div>
</template>

<script>
export default {
  name: 'VInputWithIcon',
  props: {
    value: String,
    type: {
      type: String,
      default: 'text',
    },
    prefix: {
      type: Boolean,
      default: true,
    },
    suffix: {
      type: Boolean,
      default: false,
    },
    size: {
      type: String,
      default: 'lg',
      validator(value) {
        return ['lg', 'sm'].includes(value);
      },
    },
    label: String,
    errorMessage: String,
    errorWarning: Boolean,
    inputFocus: Boolean,
    hasInput: Boolean,
    disabledIcon: {
      type: Boolean,
      default: false,
    },
  },
  data: () => ({
    focus: false,
  }),
  computed: {
    classes() {
      return {
        input: {
          'input-component__camp': true,
          'input-component__camp--error': this.errorMessage || this.errorWarning,
        },
        label: {
          'input-component__label': true,
          'input-component__label--error': this.errorMessage || this.errorWarning,
        },
        message: {
          'input-component__message': true,
        },
        activeBorder: {
          'input-component__icon-active': this.focus,
          'input-component__icon': true,
          'input-component__camp': true,
          'input-component__camp-inactive': this.disabledIcon,
        },
        container: {
          'input-component__container': true,
          'input-component__icon': true,

          'input-component__border-active': this.focus,
          'input-component__border-inactive': !this.focus,
        },
      };
    },
    attributes() {
      return {
        ...this.$attrs,
        placeholder: this.$attrs.placeholder || ' ',
        type: this.type,
      };
    },
  },
  methods: {
    handleFocus() {
      this.$emit('focus');
      this.focus = true;
    },
    handleBlur() {
      this.$emit('blur');
      this.focus = false;
    },
  },
};
</script>

<style lang="scss" scoped>
.input-component {
  display: flex;
  flex-direction: column;
  align-self: center;
  position: relative;
  width: 100%;
  border-radius: 4px 4px 0px 0px;

  &__container {
    display: flex;
    background-color: rgba(22, 22, 22, 0.04);
  }

  &__border-active {
    border-bottom: 2px solid var(--color-interaction-accent-1);
  }

  &__border-inactive {
    border-bottom: 2px solid var(--color-grey-7);
  }

  &__icon {
    padding-right: 0px !important;
    flex: 0;
  }

  &__icon-active {
    padding-right: 0px !important;
    flex: 0;

    * {
      fill: var(--color-interaction-accent-1);
    }
  }

  &__closeIcon {
    align-self: center;
    padding-right: 24px;
    cursor: pointer;
  }

  &__camp {
    transition: all 0.2s ease-in-out;
    background-color: transparent;
    padding: 22px 16px 12px 16px;
    border: 0;
    text-align: left;
    font-size: var(--font-size-3);
    width: 100%;

    &--error,
    &--error:focus {
      border-bottom: 2px solid var(--color-error);
    }

    &-inactive {
      border-bottom: 2px solid var(--color-grey-7);
      cursor: not-allowed;
      background-color: var(--color-grey-4);
      padding-right: 20px !important;
    }

    &[readonly],
    &:disabled {
      border-bottom: 2px solid var(--color-grey-7);
      cursor: not-allowed;
      background-color: var(--color-grey-4);
    }
  }

  &__label {
    pointer-events: none;
    position: absolute;
    transition: all 0.2s ease-in-out;
    color: var(--color-grey-6);

    &--error {
      color: var(--color-error) !important;
    }
  }

  &__message {
    position: relative;
    display: flex;
    align-items: center;
    font-size: var(--font-size-1);
    color: var(--color-error);
    align-self: flex-start;
    margin-top: 8px;
    padding: 0 0 0 16px;

    .message__icon {
      height: 12px;
      padding-right: 4px;
      * {
        fill: var(--color-error);
      }
    }
  }
}

.size {
  &--lg {
    .input-component {
      &__label {
        top: 0.2rem;
        font-size: var(--font-size-2);
        padding: 0 8px;
      }

      &__camp {
        height: 56px;
        font-size: var(--font-size-3);
        padding: 22px 16px 8px 16px;

        &:focus + label,
        &:not(:placeholder-shown) + label {
          font-size: 0.875rem;
          top: 0.4rem;
          color: var(--color-grey-7);
        }
      }
    }
  }

  &--sm {
    .input-component {
      &__label {
        left: 0rem;
        font-size: var(--font-size-2);
        padding: 0 8px;
      }

      &__camp {
        height: 48px;
        font-size: var(--font-size-3);
        padding: 15px 8px 6px 8px;

        &:focus + label,
        &:not(:placeholder-shown) + label {
          font-size: 0.73rem;
          top: 0.1rem;
          color: var(--color-grey-7);
        }
      }
    }
  }
}

@media (max-width: 320px) {
  .size {
    &--lg {
      .input-component {
        &__camp {
          height: 48px;
          font-size: var(--font-size-2);

          &:focus + label,
          &:not(:placeholder-shown) + label {
            font-size: 0.675rem;
          }
        }

        &__label {
          font-size: var(--font-size-2);
          top: 1rem;
        }
      }
    }

    &--sm {
      .input-component {
        &__camp {
          height: 40px;

          &:focus + label,
          &:not(:placeholder-shown) + label {
            font-size: 0.68rem;
          }
        }

        &__label {
          font-size: var(--font-size-2);
          top: 0.7rem;
        }
      }
    }
  }
}
</style>
